import { Tag, TagLabel } from '@chakra-ui/react';
import React from 'react';

import { finleyAssertUnreachable } from '@finley-technologies/common';
import { Loan } from '@finley-technologies/finley-types';

import { FundingRequestStatusEnumGQLType } from '../../../../../types/dashboard-core-gqlTypes.generated.d';
import { TagIcon } from 'packages/common-frontend/components/icons';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import { ColorScheme } from '~/packages/common-frontend/theme';

// Types
type Status = 'info' | 'success' | 'warning' | 'error';

// Helpers
const getColor = (status?: Status): ColorScheme => {
  switch (status) {
    case 'info':
      return 'blue' as const;
    case 'success':
      return 'green' as const;
    case 'warning':
      return 'orange' as const;
    case 'error':
      return 'red' as const;
    default:
      return 'gray' as const;
  }
};

// Exports
type StatusBadgeProps = {
  icon: string;
  status?: Status;
  label: string;
  TagProps?: React.ComponentProps<typeof Tag>;
};
export const StatusBadge = ({
  status,
  label,
  icon,
  TagProps,
}: StatusBadgeProps) => {
  const colorScheme = getColor(status);
  return (
    <Tag colorScheme={colorScheme} variant="solid-muted" {...TagProps}>
      <TagIcon icon={icon} />
      <TagLabel>{label}</TagLabel>
    </Tag>
  );
};

type Props = {
  status: FundingRequestStatusEnumGQLType;
  size?: React.ComponentProps<typeof Tag>['size'];
};
export const FundingRequestStatusBadge = ({ status, size = 'md' }: Props) => {
  const userType = useUserAuthOrThrow().user.userType;
  switch (status) {
    case 'unsubmitted':
    case 'pending':
      return (
        <StatusBadge
          icon="mailbox"
          label="Not Submitted"
          TagProps={{ size, minWidth: size === 'sm' ? '9rem' : '11rem' }}
        />
      );
    case 'submitted':
      return (
        <StatusBadge
          status="info"
          icon={userType === 'BORROWER' ? 'mailbox-flag-up' : 'circle-check'}
          label={userType === 'BORROWER' ? 'Submitted' : 'Ready for Review'}
          TagProps={{ size }}
        />
      );
    case 'expired':
      return (
        <StatusBadge
          icon="calendar-xmark"
          label="Expired"
          TagProps={{ size }}
        />
      );
    case 'canceled':
      return (
        <StatusBadge icon="circle-x" label="Canceled" TagProps={{ size }} />
      );
    case 'rejected':
      return (
        <StatusBadge
          status="error"
          icon="circle-x"
          label="Rejected"
          TagProps={{ size }}
        />
      );
    case 'approved':
      return (
        <StatusBadge
          status="success"
          icon="circle-check"
          label="Approved"
          TagProps={{ size }}
        />
      );
    case 'pendingLoanEdit':
    case 'canceledLoanEdit':
    case 'overriddenFromLoanEdit':
      // TODO: these should likely not be shown so defaulting to "null" for now
      // but can come back and revisit and populate as needed
      return null;
    default:
      return finleyAssertUnreachable(status);
  }
};

type LoanStatusBadgeProps = {
  status: Loan.CalculatedLoanStatus;
};
export const LoanStatusBadge = ({ status }: LoanStatusBadgeProps) => {
  switch (status) {
    case 'initiated':
      return (
        <StatusBadge status="warning" icon="power-off" label="Initiated" />
      );
    case 'funded':
      return <StatusBadge status="warning" icon="sack-dollar" label="Funded" />;
    case 'overdue':
      return (
        <StatusBadge status="error" icon="calendar-clock" label="Overdue" />
      );
    case 'pending':
      return <StatusBadge icon="mailbox" label="Pending" />;
    case 'canceled':
      return <StatusBadge status="error" icon="circle-x" label="Canceled" />;
    case 'pendingEdit':
    case 'overriddenFromEdit':
    case 'canceledEdit':
      // @LOANS TODO: need to evaluate if these are ever shown, and if so,
      // update this to reflect the proper badge
      return null;
    default:
      return finleyAssertUnreachable(status);
  }
};

export const statusConfigs: Record<
  FundingRequestStatusEnumGQLType,
  { label: string }
> = {
  approved: { label: 'Approved' },
  submitted: { label: 'Submitted' },
  pending: { label: 'Pending' },
  canceled: { label: 'Canceled' },
  unsubmitted: { label: 'Unsubmitted' },
  expired: { label: 'Expired' },
  rejected: { label: 'Rejected' },
  // *** Loan Edit Actions ***
  pendingLoanEdit: { label: 'Pending' },
  overriddenFromLoanEdit: { label: 'Canceled' },
  canceledLoanEdit: { label: 'Canceled' },
};
